import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Chat, ChatMessage } from '@app/models';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Loader2, MessageCircle } from 'lucide-react';
import { IconSendMessage } from '@app/icons';
import { MessageItem } from './components';
import { useForm } from '@app/forms';
import { createMessage, getChatMessages } from '@app/api';
import { useUserContext } from '@app/providers';
import { dateFormat } from '@app/helpers';
import { Empty } from '@app/components';

interface Props {
  chatId: string | null;
}

interface MessageData {
  message: string;
}

function UsersChat(props: Props) {
  const { chatId } = props;
  const { values, onChange, setValues } = useForm<MessageData>({
    message: '',
  });
  const { user, currentProfile } = useUserContext();

  const [chat, setChat] = useState<Chat>({} as Chat);
  const [pending, setPending] = useState<boolean>(true);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      if (!chatId || !isMountedRef.current) return;

      const data = await getChatMessages(chatId);

      if (isMountedRef.current) {
        setChat(data);
        setPending(false);
        // Scroll to bottom when new messages arrive
        setTimeout(scrollToBottom, 100);
      }
    } catch (e) {
      if (isMountedRef.current) {
        setPending(false);
      }
    }
  }, [chatId, scrollToBottom]);

  const getData = useCallback(async () => {
    // Initial fetch
    await fetchMessages();

    // Scroll to bottom after initial load
    setTimeout(scrollToBottom, 100);

    // Set up polling every 1 second
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      fetchMessages();
    }, 1000);
  }, [fetchMessages, scrollToBottom]);

  useEffect(() => {
    isMountedRef.current = true;
    getData();

    return () => {
      isMountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [getData]);

  const onSendMessage = useCallback(async () => {
    try {
      if (!chatId) return;
      const params: Partial<ChatMessage> = {
        message: values.message,
        chatId: chatId,
        authorUserId: user.id,
      };

      if (currentProfile.currentType === 'company') {
        params.authorCompanyId = currentProfile.id;
      }

      await createMessage(params);
      setValues({ message: '' });

      // Immediately fetch new messages after sending
      fetchMessages();
    } catch (e) {}
  }, [
    chatId,
    values.message,
    user.id,
    currentProfile.currentType,
    currentProfile.id,
    setValues,
    fetchMessages,
  ]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Чат</CardTitle>
      </CardHeader>
      <CardContent className="p-0 flex flex-col gap-4 h-[600px] justify-between">
        <div
          className="flex-1 bg-muted p-4 rounded-lg flex flex-col gap-5 overflow-auto px-2" //bg сб
          ref={chatContainerRef}
        >
          {pending ? (
            <div className="flex flex-col justify-center items-center h-full gap-3">
              <Loader2 className="animate-spin w-10 h-10 text-primary" />
              <p className="text-sm text-muted-foreground">
                Загрузка сообщений...
              </p>
            </div>
          ) : !chat.groupedMessage || chat.groupedMessage.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <Empty
                title="Нет сообщений"
                icon={
                  <MessageCircle className="w-16 h-16 text-muted-foreground/40" />
                }
                className="bg-transparent"
              />
            </div>
          ) : (
            chat.groupedMessage
              ?.slice()
              .reverse()
              .map((messages) => (
                <React.Fragment key={messages.date}>
                  <div className="flex justify-center my-2">
                    <span className="bg-muted rounded-xl px-2 py-1 text-xs font-normal">
                      {dateFormat(messages.date)}
                    </span>
                  </div>
                  {messages.messages?.map((message) => (
                    <MessageItem
                      key={message.id}
                      message={message}
                      user={user}
                    />
                  ))}
                </React.Fragment>
              ))
          )}
        </div>
        <form
          className="sticky bottom-0 bg-background border-t p-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSendMessage();
          }}
        >
          <div className="flex gap-2 items-center">
            <Input
              className="flex-1"
              placeholder="Введите сообщение"
              value={values.message}
              onChange={(e) => onChange(e.target.value, 'message')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSendMessage();
                }
              }}
            />
            <Button type="submit" size="icon" variant="default">
              <IconSendMessage />
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default memo(UsersChat);
